/*
 * Kent Outdoors EMEA — consent-aware Shopify order attribution capture v1.
 *
 * Install once in each storefront theme after privacy/legal review. This code
 * reads Shopify's Customer Privacy API; it never grants or changes consent.
 * Marketing identifiers are written only when both marketing and data-sharing
 * processing are allowed. Private __sf_* cart attributes flow to the order and
 * are visually hidden at checkout. No URL, email, phone or address is stored.
 *
 * This is an evidence capture, not a pixel and not a conversion sender.
 */
(function () {
  "use strict";

  var VERSION = "kent-order-attribution-v1";
  var STORAGE_FIRST = "kent_attr_first_v1";
  var STORAGE_LAST = "kent_attr_last_v1";
  var CLOCK_SKEW_MS = 5 * 60 * 1000;
  var MAX_TOUCH_AGE_MS = 90 * 24 * 60 * 60 * 1000;
  var META_CLICK_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  var UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
  var CLICK_KEYS = ["gclid", "gbraid", "wbraid", "fbclid"];
  var TOUCH_KEYS = UTM_KEYS.concat(CLICK_KEYS).concat(["fbc", "fbp"]);
  var pageSignals = signalsFromLocation();
  var lastPayload = null;

  function clean(value, maxLength) {
    if (value === null || value === undefined) return null;
    var text = String(value).replace(/[\u0000-\u001f\u007f]+/g, " ").trim();
    return text ? text.slice(0, maxLength || 240) : null;
  }

  function signalsFromLocation() {
    var out = { observed_at: new Date().toISOString() };
    try {
      var params = new URLSearchParams(window.location.search || "");
      UTM_KEYS.forEach(function (key) {
        var value = clean(params.get(key), 240);
        if (value) out[key] = value;
      });
      CLICK_KEYS.forEach(function (key) {
        var value = clean(params.get(key), 500);
        if (value) out[key] = value;
      });
    } catch (_error) {}
    return out;
  }

  function hasIdentity(signals) {
    return TOUCH_KEYS.some(function (key) { return Boolean(signals && signals[key]); });
  }

  function cookie(name) {
    var prefix = name + "=";
    var parts = String(document.cookie || "").split(";");
    for (var i = 0; i < parts.length; i += 1) {
      var part = parts[i].trim();
      if (part.indexOf(prefix) === 0) {
        try { return clean(decodeURIComponent(part.slice(prefix.length)), 500); }
        catch (_error) { return null; }
      }
    }
    return null;
  }

  function oneTrustConsentId() {
    var value = cookie("OptanonConsent");
    if (!value) return null;
    try {
      var consentId = clean(new URLSearchParams(value).get("consentId"), 64);
      return consentId && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(consentId)
        ? consentId.toLowerCase() : null;
    } catch (_error) {
      return null;
    }
  }

  function observedAtMs(signals) {
    var parsed = Date.parse(signals && signals.observed_at);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function isFresh(signals, maxAgeMs) {
    var observed = observedAtMs(signals);
    var now = Date.now();
    return observed !== null && observed <= now + CLOCK_SKEW_MS && now - observed <= maxAgeMs;
  }

  function freshFbc(value) {
    var cleaned = clean(value, 500);
    var match = cleaned && cleaned.match(/^fb\.1\.(\d{10,13})\.[A-Za-z0-9._%-]+$/);
    if (!match) return null;
    var observed = Number(match[1]);
    if (match[1].length === 10) observed *= 1000;
    var now = Date.now();
    return observed <= now + CLOCK_SKEW_MS && now - observed <= META_CLICK_AGE_MS
      ? cleaned : null;
  }

  function enrichMeta(signals) {
    var copy = Object.assign({}, signals || {});
    var fbc = freshFbc(cookie("_fbc"));
    var fbp = cookie("_fbp");
    if (fbc) copy.fbc = fbc;
    // If the Meta cookie has not been created yet, retain an fbc using the
    // actual landing observation time—not the later order timestamp.
    if (!copy.fbc && copy.fbclid && isFresh(copy, META_CLICK_AGE_MS)) {
      copy.fbc = "fb.1." + Date.parse(copy.observed_at) + "." + copy.fbclid;
    }
    if (fbp) copy.fbp = fbp;
    return copy;
  }

  function loadStored(key) {
    try {
      var value = JSON.parse(window.localStorage.getItem(key) || "null");
      if (!value || typeof value !== "object" || !isFresh(value, MAX_TOUCH_AGE_MS)) {
        window.localStorage.removeItem(key);
        return null;
      }
      // Meta click attribution is bounded more tightly than the general
      // first/last-touch evidence window. Never refresh an old fbclid/fbc merely
      // because a visitor returned to the storefront.
      if (!isFresh(value, META_CLICK_AGE_MS)) {
        delete value.fbclid;
        delete value.fbc;
      } else if (value.fbc) {
        value.fbc = freshFbc(value.fbc);
      }
      window.localStorage.setItem(key, JSON.stringify(value));
      return value;
    } catch (_error) {
      return null;
    }
  }

  function saveStored(key, value) {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (_error) {}
  }

  function clearStored() {
    try {
      window.localStorage.removeItem(STORAGE_FIRST);
      window.localStorage.removeItem(STORAGE_LAST);
    } catch (_error) {}
  }

  function decision(value) {
    if (value === "yes") return "granted";
    if (value === "no") return "denied";
    return "undecided";
  }

  function boolText(value) {
    return value === true ? "true" : value === false ? "false" : "unknown";
  }

  function consentState(api) {
    var selected = {};
    try { selected = api.currentVisitorConsent() || {}; } catch (_error) {}
    function allowed(method) {
      try { return Boolean(api[method]()); } catch (_error) { return null; }
    }
    var region = null;
    try { region = clean(api.getRegion(), 5); } catch (_error) {}
    return {
      analytics: decision(selected.analytics),
      marketing: decision(selected.marketing),
      preferences: decision(selected.preferences),
      sale_of_data: decision(selected.sale_of_data),
      analytics_allowed: allowed("analyticsProcessingAllowed"),
      marketing_allowed: allowed("marketingAllowed"),
      preferences_allowed: allowed("preferencesProcessingAllowed"),
      sale_of_data_allowed: allowed("saleOfDataAllowed"),
      region: region ? region.toUpperCase() : null,
      observed_at: new Date().toISOString()
    };
  }

  function setTouch(attributes, prefix, signals) {
    if (!signals) return;
    attributes["__sf_" + prefix + "_touch_observed_at"] = clean(signals.observed_at, 64) || "";
    TOUCH_KEYS.forEach(function (key) {
      attributes["__sf_" + prefix + "_" + key] = clean(signals[key], key.indexOf("utm_") === 0 ? 240 : 500) || "";
    });
  }

  function clearTouch(attributes, prefix) {
    attributes["__sf_" + prefix + "_touch_observed_at"] = "";
    TOUCH_KEYS.forEach(function (key) { attributes["__sf_" + prefix + "_" + key] = ""; });
  }

  function cartUpdate(attributes) {
    var serialised = JSON.stringify(attributes);
    if (serialised === lastPayload) return Promise.resolve();
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return fetch(root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ attributes: attributes }),
      credentials: "same-origin",
      keepalive: true
    }).then(function (response) {
      if (!response.ok) throw new Error("cart attribution update failed");
      // Suppress only a payload known to have reached Shopify. A failed update
      // remains retryable on the next consent event/page execution.
      lastPayload = serialised;
    }).catch(function () {
      // Purchase delivery must never be blocked by measurement evidence capture.
    });
  }

  function persist(api) {
    var consent = consentState(api);
    var oneTrustId = oneTrustConsentId();
    var attributes = {
      "__sf_capture_version": VERSION,
      "__sf_identity_observed_at": new Date().toISOString(),
      "__sf_consent_analytics": consent.analytics,
      "__sf_consent_marketing": consent.marketing,
      "__sf_consent_preferences": consent.preferences,
      "__sf_consent_sale_of_data": consent.sale_of_data,
      "__sf_allowed_analytics": boolText(consent.analytics_allowed),
      "__sf_allowed_marketing": boolText(consent.marketing_allowed),
      "__sf_allowed_preferences": boolText(consent.preferences_allowed),
      "__sf_allowed_sale_of_data": boolText(consent.sale_of_data_allowed),
      "__sf_consent_observed_at": consent.observed_at,
      "__sf_consent_source": "shopify_customer_privacy_api",
      // This anonymous provider reference is never itself treated as consent.
      // The server must fetch and hash the matching OneTrust receipt over OAuth.
      "__sf_consent_provider": oneTrustId ? "onetrust" : "",
      "__sf_onetrust_consent_id": oneTrustId || "",
      "__sf_consent_region": consent.region || "",
      // Shopify's scopes are not silently relabelled as Google Consent Mode.
      "__sf_google_ad_user_data_consent": "unknown",
      "__sf_google_ad_personalization_consent": "unknown"
    };

    var attributionAllowed = (
      consent.analytics_allowed === true &&
      consent.marketing_allowed === true &&
      consent.sale_of_data_allowed === true
    );
    if (!attributionAllowed) {
      clearStored();
      clearTouch(attributes, "first");
      clearTouch(attributes, "last");
      return cartUpdate(attributes);
    }

    var observed = enrichMeta(pageSignals);
    var first = loadStored(STORAGE_FIRST);
    var last = loadStored(STORAGE_LAST);
    if (hasIdentity(observed)) {
      if (!first) {
        first = observed;
        saveStored(STORAGE_FIRST, first);
      }
      last = observed;
      saveStored(STORAGE_LAST, last);
    }
    setTouch(attributes, "first", first);
    setTouch(attributes, "last", last);
    return cartUpdate(attributes);
  }

  function ready() {
    var api = window.Shopify && window.Shopify.customerPrivacy;
    if (!api) return;
    persist(api);
    document.addEventListener("visitorConsentCollected", function () { persist(api); });
  }

  if (window.Shopify && window.Shopify.customerPrivacy) {
    ready();
  } else if (window.Shopify && window.Shopify.loadFeatures) {
    window.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      function (error) { if (!error) ready(); }
    );
  }
})();
